import * as XLSX from 'xlsx';

export const TEMPLATE_HEADERS = [
  'System Name',           // 1  required
  'Vendor / Customer #',   // 2  required
  'Vendor / Customer Name',// 3  required
  'Name Line 2',           // 4  optional
  'Address Seq',           // 5  optional — blank defaults to "1"
  'Address Line 1',        // 6  optional
  'Address Line 2',        // 7  optional
  'Address Line 3',        // 8  optional
  'Address Line 4',        // 9  optional
  'PO Box',                // 10 optional
  'City',                  // 11 optional
  'State / Province',      // 12 optional
  'ZIP / Postal Code',     // 13 optional
  'Country',               // 14 optional
  'Phone',                 // 15 optional
  'Email',                 // 16 optional
  'Tax ID',                // 17 optional
  'Website Domain',        // 18 optional
];

// Single-address vendor — Address Seq and PO Box blank (both optional)
export const SAMPLE_ROW = [
  'SAP', 'V-10042', 'Acme Corp', '', '', '123 Main St Ste 400', '', '', '', '',
  'Chicago', 'IL', '60601', 'US', '(312) 555-1234', 'ap@acme.com', '36-1234567', 'acme.com',
];

const REQUIRED = new Set(['System Name', 'Vendor / Customer #', 'Vendor / Customer Name']);
const MAX_ROWS  = 50000;
const MAX_EXAMPLES = 5; // max row numbers to show per field

// Per-field validators — return a string if there's an issue, null if OK
const FIELD_VALIDATORS = {
  'System Name': (v) => (!v ? 'blank — enter your ERP/system name (e.g. SAP, Oracle)' : null),
  'Vendor / Customer #':    (v) => (!v ? 'blank — every row needs an internal vendor/customer ID' : null),
  'Vendor / Customer Name': (v) => (!v ? 'blank — every row must have a vendor/customer name' : null),
  'Email': (v) => {
    if (!v) return null;
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v) ? null : `"${v}" doesn't look like a valid email`;
  },
  'Website Domain': (v) => {
    if (!v) return null;
    if (v.startsWith('http') || v.toLowerCase().startsWith('www.'))
      return `"${v}" — use just the domain (e.g. acme.com), no http:// or www.`;
    return null;
  },
};

// Which fields produce errors (blocking) vs warnings (non-blocking)
const ERROR_FIELDS = new Set(['System Name', 'Vendor / Customer #', 'Vendor / Customer Name']);

// Scan all sheets and return the first one that has the required header columns.
// Handles multi-tab workbooks where data isn't on the first sheet.
function findDataSheet(wb) {
  for (const name of wb.SheetNames) {
    const ws   = wb.Sheets[name];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
    for (let i = 0; i < Math.min(6, rows.length); i++) {
      const row = rows[i].map(String);
      if (row.includes('Vendor / Customer #') || row.includes('System Name')) {
        return { ws, rows };
      }
    }
  }
  // Fallback: first sheet
  const ws   = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
  return { ws, rows };
}

// Check if headers match exactly; returns { exact, userHeaders, rowCount } or null on parse error
export async function checkColumnMapping(file) {
  try {
    const ext = file.name.split('.').pop().toLowerCase();
    let wb;
    if (ext === 'csv') {
      const text = await file.text();
      wb = XLSX.read(text, { type: 'string' });
    } else {
      const buf = await file.arrayBuffer();
      wb = XLSX.read(new Uint8Array(buf), { type: 'array' });
    }
    const { rows } = findDataSheet(wb);
    if (!rows || rows.length < 2) return null;

    // Find header row within the sheet
    let headerIdx = 0;
    for (let i = 0; i < Math.min(6, rows.length); i++) {
      if (rows[i].map(String).some(c => c === 'Vendor / Customer #' || c === 'System Name')) { headerIdx = i; break; }
    }
    const userHeaders = rows[headerIdx].map(h => String(h).trim());
    const dataRows    = rows.slice(headerIdx + 1).filter(r => r.some(c => String(c).trim() !== ''));
    const exact       = userHeaders.length === TEMPLATE_HEADERS.length &&
                        userHeaders.every((h, i) => h === TEMPLATE_HEADERS[i]);

    return { exact, userHeaders, rowCount: dataRows.length };
  } catch {
    return null;
  }
}

// Remap the file's header row to TEMPLATE_HEADERS by column position; returns new File
export async function remapFileHeaders(file) {
  try {
    const ext = file.name.split('.').pop().toLowerCase();
    let wb;
    if (ext === 'csv') {
      const text = await file.text();
      wb = XLSX.read(text, { type: 'string' });
    } else {
      const buf = await file.arrayBuffer();
      wb = XLSX.read(new Uint8Array(buf), { type: 'array' });
    }
    const { rows } = findDataSheet(wb);
    let headerIdx = 0;
    for (let i = 0; i < Math.min(6, rows.length); i++) {
      if (rows[i].map(String).some(c => c === 'Vendor / Customer #' || c === 'System Name')) { headerIdx = i; break; }
    }
    // Strip any rows before the header row, replace header with template headers
    const trimmed = rows.slice(headerIdx);
    const newRows    = trimmed.map((r, i) => i === 0 ? [...TEMPLATE_HEADERS] : r);
    const newWs      = XLSX.utils.aoa_to_sheet(newRows);
    const newWb      = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(newWb, newWs, 'Sheet1');
    const wbOut      = XLSX.write(newWb, { type: 'array', bookType: 'xlsx' });
    const blob       = new Blob([wbOut], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    return new File([blob], file.name.replace(/\.[^.]+$/, '.xlsx'), { type: blob.type });
  } catch {
    return file;
  }
}

export async function validateUploadFile(file) {
  // Returns:
  // {
  //   valid: bool,
  //   structureErrors: string[],   // file-level blocking problems
  //   fields: [{name, required, status, count, examples, sampleMessage}],  // one entry per TEMPLATE_HEADERS
  //   rowCount: number,
  //   totalErrors: number,
  //   totalWarnings: number,
  // }
  const structureErrors = [];

  try {
    const ext = file.name.split('.').pop().toLowerCase();
    let wb;
    if (ext === 'csv') {
      const text = await file.text();
      wb = XLSX.read(text, { type: 'string' });
    } else {
      const buf = await file.arrayBuffer();
      wb = XLSX.read(new Uint8Array(buf), { type: 'array' });
    }

    const { rows } = findDataSheet(wb);

    if (!rows || rows.length === 0) {
      return { valid: false, structureErrors: ['The file appears to be empty.'], fields: [], rowCount: 0, totalErrors: 0, totalWarnings: 0 };
    }

    // Find header row
    let headerIdx = -1;
    for (let i = 0; i < Math.min(6, rows.length); i++) {
      const row = rows[i].map(String);
      if (row.includes('Vendor / Customer #') || row.includes('System Name')) { headerIdx = i; break; }
    }
    if (headerIdx === -1) {
      return {
        valid: false,
        structureErrors: ['Could not find the column headers. Make sure your file uses the MasterFileAI template with "System Name" and "Vendor / Customer #" columns.'],
        fields: [], rowCount: 0, totalErrors: 0, totalWarnings: 0,
      };
    }

    const headers  = rows[headerIdx].map(h => String(h).trim());
    const dataRows = rows.slice(headerIdx + 1).filter(r => r.some(c => String(c).trim() !== ''));

    // Column count check — accept 16 (legacy), 17, or 18 (current full template)
    if (headers.length < 16 || headers.length > 18) {
      structureErrors.push(
        `Your file has ${headers.length} column${headers.length !== 1 ? 's' : ''} — expected 17 or 18 (Address Seq and PO Box are optional). ` +
        `Check that no columns were added, removed, or reordered.`
      );
    }

    // Missing required columns
    const missingCols = TEMPLATE_HEADERS.filter(h => REQUIRED.has(h) && !headers.includes(h));
    missingCols.forEach(c => structureErrors.push(`Required column "${c}" is missing.`));

    if (structureErrors.length > 0) {
      return { valid: false, structureErrors, fields: [], rowCount: dataRows.length, totalErrors: 0, totalWarnings: 0 };
    }

    if (dataRows.length === 0) {
      return {
        valid: false,
        structureErrors: ['Your file has headers but no data rows. Add at least one record before uploading.'],
        fields: [], rowCount: 0, totalErrors: 0, totalWarnings: 0,
      };
    }

    if (dataRows.length > MAX_ROWS) {
      return {
        valid: false,
        structureErrors: [`Your file has ${dataRows.length.toLocaleString()} rows — the maximum is 50,000. Please split it into smaller batches.`],
        fields: [], rowCount: dataRows.length, totalErrors: 0, totalWarnings: 0,
      };
    }

    // Build column index map
    const ci = {};
    TEMPLATE_HEADERS.forEach(h => { ci[h] = headers.indexOf(h); });

    // Per-field issue accumulators: { [fieldName]: { rows: number[], firstMessage: string } }
    const fieldIssues = {};
    TEMPLATE_HEADERS.forEach(h => { fieldIssues[h] = { rows: [], firstMessage: null }; });

    // Scan every row, accumulate per-field
    for (let i = 0; i < dataRows.length; i++) {
      const row    = dataRows[i];
      const rowNum = i + headerIdx + 2;
      for (const [field, validator] of Object.entries(FIELD_VALIDATORS)) {
        const val = String(row[ci[field]] ?? '').trim();
        const msg = validator(val);
        if (msg) {
          const bucket = fieldIssues[field];
          if (bucket.rows.length < MAX_EXAMPLES) bucket.rows.push(rowNum);
          if (!bucket.firstMessage) bucket.firstMessage = msg;
          bucket.count = (bucket.count || 0) + 1;
        }
      }
    }

    // Build per-field summary (one entry per TEMPLATE_HEADERS)
    const fields = TEMPLATE_HEADERS.map(name => {
      const bucket  = fieldIssues[name];
      const count   = bucket.count || 0;
      const isError = ERROR_FIELDS.has(name);
      const hasValidator = !!FIELD_VALIDATORS[name];

      let status = 'ok';
      if (count > 0) status = isError ? 'error' : 'warning';
      else if (!hasValidator) status = 'unchecked'; // optional, no validation rule

      return {
        name,
        required: REQUIRED.has(name),
        status,          // 'ok' | 'error' | 'warning' | 'unchecked'
        count,
        examples: bucket.rows,           // up to MAX_EXAMPLES row numbers
        totalInFile: dataRows.length,
        sampleMessage: bucket.firstMessage,
      };
    });

    const totalErrors   = fields.filter(f => f.status === 'error').reduce((s, f) => s + f.count, 0);
    const totalWarnings = fields.filter(f => f.status === 'warning').reduce((s, f) => s + f.count, 0);

    return {
      valid: totalErrors === 0,
      structureErrors: [],
      fields,
      rowCount: dataRows.length,
      totalErrors,
      totalWarnings,
    };

  } catch {
    return {
      valid: false,
      structureErrors: ["Couldn't read your file. Make sure it's a valid .xlsx or .csv file and isn't password-protected."],
      fields: [], rowCount: 0, totalErrors: 0, totalWarnings: 0,
    };
  }
}
